import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import debounce from 'lodash/debounce'
import groupBy from 'lodash/groupBy'
import xor from 'lodash/xor'
import { toDay, toHour } from '~/assets/date'
import { toLocalDateString } from '~/assets/util'

// Provide a single debounce that we can wrap around all fetch calls
const fetchDebounce = debounce((call: () => void) => call(), 300)

const normalizeSuggestionValues = (value: any) =>
  (value === undefined || value === null ? [] : Array.isArray(value) ? value : [value])
    .map((item: any) => String(item).trim())
    .filter((item: string) => item !== '')
    .sort()

// overrides.q allows the component to pass the live draft keyword (before submit)
const buildSuggestionParams = (state: any, overrides: any = {}) => {
  const keyword = 'q' in overrides ? String(overrides.q ?? '').trim() : typeof state.keyword === 'string' ? state.keyword.trim() : ''

  return {
    q: keyword,
    concertTitles: normalizeSuggestionValues(state.concertTitles),
    date: state.date,
    limit: Math.min(20, Math.max(1, Number(overrides.limit) || 10)),
    fuzziness: keyword.length <= 1 ? '0' : 'AUTO',
    composers: normalizeSuggestionValues(state.composers),
    discounts: normalizeSuggestionValues(state.discounts),
    genres: normalizeSuggestionValues(state.genres),
    instruments: normalizeSuggestionValues(state.instruments),
    musicians: normalizeSuggestionValues(state.musicians),
    organizers: normalizeSuggestionValues(state.organizers),
    rooms: normalizeSuggestionValues(state.rooms),
    specials: normalizeSuggestionValues(state.specials),
    tags: normalizeSuggestionValues(state.tags),
    works: normalizeSuggestionValues(state.works),
  }
}

const buildSuggestionSignature = (state: any, overrides: any = {}) => JSON.stringify(buildSuggestionParams(state, overrides))

const buildSearchTrackingParams = (state: any, overrides: any = {}) => {
  const pickValue = (key: string, fallback: any) => (key in overrides ? overrides[key] : fallback)

  return {
    composers: normalizeSuggestionValues(pickValue('composers', state.composers)),
    concertTitles: normalizeSuggestionValues(pickValue('concertTitles', state.concertTitles)),
    date: pickValue('date', state.date),
    discounts: normalizeSuggestionValues(pickValue('discounts', state.discounts)),
    genres: normalizeSuggestionValues(pickValue('genres', state.genres)),
    instruments: normalizeSuggestionValues(pickValue('instruments', state.instruments)),
    keyword: String(pickValue('keyword', state.keyword) ?? '').trim(),
    musicians: normalizeSuggestionValues(pickValue('musicians', state.musicians)),
    organizers: normalizeSuggestionValues(pickValue('organizers', state.organizers)),
    rooms: normalizeSuggestionValues(pickValue('rooms', state.rooms)),
    specials: normalizeSuggestionValues(pickValue('specials', state.specials)),
    tags: normalizeSuggestionValues(pickValue('tags', state.tags)),
    works: normalizeSuggestionValues(pickValue('works', state.works)),
  }
}

const buildSearchTrackingSignature = (state: any, overrides: any = {}) => JSON.stringify(buildSearchTrackingParams(state, overrides))

/**
 * Concert agenda (event) overview state. The module-level helpers below take a plain state object, so
 * `stateSnapshot()` builds one from the refs.
 */
export const useEventOverviewStore = defineStore('event-overview', () => {
  const cacheStore = useCacheStore()
  const pageStore = usePageStore()
  const cmsFetch = useCmsFetch()
  const graphqlFetch = useGraphqlFetch()
  const { locale } = useI18n()

  // Per-request fetch-staleness guard (not a plain module-level `let`): the module is a Node-process-wide
  // singleton shared across every SSR request, while this store's setup runs fresh per request, so keeping
  // it here avoids one visitor's in-flight fetch corrupting another's staleness check.
  const latestFetchRequestId = ref(0)

  /*
   * Facet maps are keyed by category (genres, rooms, composers, ...). Which categories come back is
   * decided by Elasticsearch, so the key set is not fixed; consumers treat a missing category as empty.
   */
  type FacetBuckets = Record<string, { value?: string; label?: string; count?: number }[]>

  // Global results (i.e. unaffected by search context)
  const allFacets = ref<FacetBuckets>({})

  // Search context
  const composers = ref<string[]>([])
  const date = ref(toHour('now').toISOString())
  const discounts = ref<string[]>([])
  const genres = ref<string[]>([])
  const instruments = ref<string[]>([])
  const keyword = ref('')
  const organizers = ref<string[]>([])
  const concertTitles = ref<string[]>([])
  const musicians = ref<string[]>([])
  const works = ref<string[]>([])
  const page = ref(1)
  const rooms = ref<string[]>([])
  const specials = ref<string[]>([])
  const tags = ref<string[]>([])

  // Banner results
  const fallbackBanners = ref<any[]>([])
  const banners = ref<any[]>([])

  // Search results
  const events = ref<any[]>([]) // Events ordered by EventDate
  /*
   * Raw per-search facets from Elasticsearch, named differently from the `facets` getter that exposes
   * it - Pinia shares one namespace for state and getters, so they can't share a name.
   */
  const rawFacets = ref<FacetBuckets>({})
  const separateFacets = ref<FacetBuckets>({})
  const hits = ref<string[]>([]) // Event uri's ordered by EventDate
  const loading = ref(true)
  const lastCompletedSearchTrackingSignature = ref('')
  const keywordSuggestions = ref<any[]>([])
  const topEvents = ref<any[]>([]) // Events ordered by relevance
  const topHits = ref<string[]>([]) // Event uri's ordered by relevance
  const totalHits = ref(0)
  /*
   * Set to true when the user commits a search from the homepage search component, so the agenda page
   * can fire search_performed with the real result count once the search settles.
   */
  const pendingSearchPerformed = ref(false)

  // Snapshot of the search context, passed to the module-level `build*` helpers (which expect a plain
  // state object rather than refs).
  const stateSnapshot = () => ({
    composers: composers.value,
    concertTitles: concertTitles.value,
    date: date.value,
    discounts: discounts.value,
    genres: genres.value,
    instruments: instruments.value,
    keyword: keyword.value,
    musicians: musicians.value,
    organizers: organizers.value,
    rooms: rooms.value,
    specials: specials.value,
    tags: tags.value,
    works: works.value,
  })

  // Constants
  const hitsPerPage = computed(() => 15)

  const dateIsSpecified = computed(() => toHour(date.value).toISOString() !== toHour('now').toISOString())
  const searchTrackingSignature = computed(() => buildSearchTrackingSignature(stateSnapshot()))

  // Url parameters
  const urlParameters = computed(() => ({
    composers: composers.value,
    concertTitles: concertTitles.value,
    date: date.value === toHour('now').toISOString() ? undefined : toLocalDateString(date.value),
    discounts: discounts.value,
    genres: genres.value,
    instruments: instruments.value,
    keyword: keyword.value || undefined,
    musicians: musicians.value,
    organizers: organizers.value,
    page: page.value === 1 ? undefined : page.value,
    rooms: rooms.value,
    specials: specials.value,
    tags: tags.value,
    works: works.value,
  }))

  // Banner results
  const bannersAtPosition = computed(() => {
    return {
      // First the fallback banners
      ...Object.fromEntries(fallbackBanners.value.map((banner) => [banner.position, banner])),
      // Second the banners based on search criteria, these will overwrite the fallback banners
      ...Object.fromEntries(banners.value.map((banner) => [banner.position, banner])),
    }
  })

  // Search results
  const facets = computed(() => {
    // All three may be undefined: Elasticsearch decides which categories it returns.
    const helper = (fromAllFacets: any[] | undefined, filteredFacets: any[] | undefined, categorySeparateFacets: any[] | undefined, activeList: string[]) => {
      const countSource = activeList.length > 0 ? categorySeparateFacets : filteredFacets
      const currentCountMap = Object.fromEntries((countSource || []).map(({ count, label, value }) => [value || label, count]))
      return (fromAllFacets || []).map(({ value, label }) => ({
        active: activeList.includes(value || label),
        count: currentCountMap[value || label] || 0,
        label,
        value: value || label,
      }))
    }
    return [
      { key: 'genres', values: helper(allFacets.value.genres, rawFacets.value.genres, separateFacets.value.genres, genres.value) },
      { key: 'rooms', values: helper(allFacets.value.rooms, rawFacets.value.rooms, separateFacets.value.rooms, rooms.value) },
      { key: 'composers', values: helper(allFacets.value.composers, rawFacets.value.composers, separateFacets.value.composers, composers.value) },
      { key: 'instruments', values: helper(allFacets.value.instruments, rawFacets.value.instruments, separateFacets.value.instruments, instruments.value) },
      { key: 'specials', values: helper(allFacets.value.specials, rawFacets.value.specials, separateFacets.value.specials, specials.value) },
      { key: 'discounts', values: helper(allFacets.value.discounts, rawFacets.value.discounts, separateFacets.value.discounts, discounts.value) },
    ]
  })
  const activeFacets = computed(() => {
    const helper = (fromAllFacets: any[] | undefined, activeList: string[], facet: string) => {
      const fromAll = (fromAllFacets || []).filter(({ value, label }) => activeList.includes(value || label)).map(({ value, label }) => ({ facet, label, value: value || label }))
      const known = new Set(fromAll.map(({ value }) => value))
      const extras = activeList.filter((value) => !known.has(value)).map((value) => ({ facet, label: value, value }))
      return [...fromAll, ...extras]
    }
    return [
      // 'tags' are special as we do not display them as facets to the user, but they must be removable
      ...tags.value.map((value) => ({ facet: 'tags', label: value, value })),
      ...helper(allFacets.value.genres, genres.value, 'genres'),
      ...helper(allFacets.value.rooms, rooms.value, 'rooms'),
      ...helper(allFacets.value.composers, composers.value, 'composers'),
      ...helper(allFacets.value.instruments, instruments.value, 'instruments'),
      ...helper(allFacets.value.specials, specials.value, 'specials'),
      ...helper(allFacets.value.discounts, discounts.value, 'discounts'),
      ...helper(undefined, organizers.value, 'organizers'),
      ...helper(undefined, concertTitles.value, 'concertTitles'),
      ...helper(undefined, musicians.value, 'musicians'),
      ...helper(undefined, works.value, 'works'),
    ]
  })
  const eventsByDay = computed(() =>
    Object.entries(
      groupBy(
        events.value.map((event, index) => ({ ...event, index })),
        (event) => toDay(event.eventDate).toISOString(),
      ),
    ).map(([groupDate, groupEvents]) => ({
      date: groupDate,
      events: groupEvents.sort((a, b) =>
        a.eventDate === b.eventDate ? a.room.title.localeCompare(b.room.title) : new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime(),
      ),
    })),
  )

  // Global results (i.e. unaffected by search context)
  function setAllFacets(newAllFacets: any) {
    allFacets.value = newAllFacets
  }

  // Initialize
  function initialize({
    composers: cmp,
    date: dt,
    discounts: dsc,
    genres: gnr,
    instruments: ins,
    keyword: kw,
    organizers: org,
    concertTitles: cts,
    musicians: msc,
    page: pg,
    rooms: rms,
    search,
    specials: spc,
    tags: tgs,
    works: wrk,
    // 'discounts' and 'rooms' were previously available using different parameters.
    // We take this into account for backwards compatability.
    importDiscountGroups: backwardCompatibleDiscounts,
    room: backwardCompatibleRooms,
  }: any) {
    const toArray = (value: any) => (value === undefined ? [] : Array.isArray(value) ? value : [value])
    const toBackwardCompatibleArray = (value: any, backwardCompatibleValue: any) => (value === undefined ? toArray(backwardCompatibleValue) : toArray(value))
    composers.value = toArray(cmp)
    date.value = toHour(dt, 'now').toISOString()
    discounts.value = toBackwardCompatibleArray(dsc, backwardCompatibleDiscounts)
    genres.value = toArray(gnr)
    instruments.value = toArray(ins)
    keyword.value = typeof kw === 'string' ? kw : typeof search === 'string' ? search : '' // For backwards compatability we fallback to the parameter 'search' for the keyword
    organizers.value = toArray(org)
    concertTitles.value = toArray(cts)
    musicians.value = toArray(msc)
    works.value = toArray(wrk)
    page.value = typeof pg === 'string' ? Math.max(1, Number.parseInt(pg)) : 1
    rooms.value = toBackwardCompatibleArray(rms, backwardCompatibleRooms)
    specials.value = toArray(spc)
    tags.value = toArray(tgs)
  }

  // Url parameters
  function setUrlParameters(query: any) {
    const toArray = (value: any) => (value === undefined ? [] : Array.isArray(value) ? value : [value])
    const toValue = (value: any, fallback: any) => (value === undefined ? fallback : Array.isArray(value) ? value?.[0] : value)
    date.value = toHour(query.date, 'now').toISOString()
    discounts.value = toArray(query.discounts)
    genres.value = toArray(query.genres)
    instruments.value = toArray(query.instruments)
    keyword.value = toValue(query.keyword, '')
    organizers.value = toArray(query.organizers)
    concertTitles.value = toArray(query.concertTitles)
    musicians.value = toArray(query.musicians)
    works.value = toArray(query.works)
    page.value = Math.max(1, toValue(query.page, 1))
    rooms.value = toArray(query.rooms)
    specials.value = toArray(query.specials)
    tags.value = toArray(query.tags)
    fetchEvents()
    fetchBanners()
  }

  // Search context
  function reset() {
    // Search context
    composers.value = []
    date.value = toHour('now').toISOString()
    discounts.value = []
    genres.value = []
    instruments.value = []
    keyword.value = ''
    organizers.value = []
    concertTitles.value = []
    musicians.value = []
    works.value = []
    page.value = 1
    rooms.value = []
    specials.value = []
    // Banner results
    fallbackBanners.value = []
    banners.value = []
    // Search results
    events.value = []
    rawFacets.value = {}
    separateFacets.value = {}
    hits.value = []
    loading.value = true
    lastCompletedSearchTrackingSignature.value = ''
    keywordSuggestions.value = []
    topEvents.value = []
    topHits.value = []
    totalHits.value = 0
    // note that we do NOT reset the state.tags because this is not a facet that can be set by the user
    setTimeout(() => {
      fetchEvents()
      fetchBanners()
    }, 10)
  }
  function setDate(newDate?: any) {
    date.value = newDate ? toHour(newDate, 'now').toISOString() : toHour('now').toISOString()
    page.value = 1
    fetchEvents()
  }
  function setKeyword(newKeyword: string) {
    keyword.value = newKeyword
    page.value = 1
    // Debounce because we do not want to fetch on every keystroke
    fetchDebounce(() => fetchEvents())
  }
  function toggleFacet({ name, value }: { name: string; value: string }) {
    switch (name) {
      case 'composers':
        composers.value = xor(composers.value, [value])
        page.value = 1
        fetchEvents()
        fetchBanners()
        break
      case 'discounts':
        discounts.value = xor(discounts.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'genres':
        // Vue.set(state, 'genres', xor(state.genres, [value]))
        genres.value = xor(genres.value, [value])
        page.value = 1
        fetchEvents()
        fetchBanners()
        break
      case 'instruments':
        instruments.value = xor(instruments.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'rooms':
        rooms.value = xor(rooms.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'organizers':
        organizers.value = xor(organizers.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'concertTitles':
        concertTitles.value = xor(concertTitles.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'musicians':
        musicians.value = xor(musicians.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'works':
        works.value = xor(works.value, [value])
        page.value = 1
        fetchEvents()
        break
      case 'specials':
        specials.value = xor(specials.value, [value])
        page.value = 1
        fetchEvents()
        fetchBanners()
        break
      case 'tags':
        tags.value = xor(tags.value, [value])
        page.value = 1
        fetchEvents()
        break
      default:
        console.error(`Unable to toggle unknown facet "${name}" with value "${value}"`)
        break
    }
  }
  function setPage(newPage: number) {
    page.value = Math.max(1, newPage)
    fetchEvents()
  }

  // Banner results
  function setFallbackBanners(newFallbackBanners: any[]) {
    fallbackBanners.value = newFallbackBanners
  }
  function setBanners(newBanners: any[]) {
    banners.value = newBanners
  }

  // Search results
  function setEvents(newEvents: any[]) {
    events.value = newEvents
  }
  function setFacets(newFacets: any) {
    rawFacets.value = newFacets
  }
  function setSeparateFacets(newSeparateFacets: any) {
    separateFacets.value = newSeparateFacets
  }
  function setHits(newHits: string[]) {
    hits.value = newHits
  }
  function setLastCompletedSearchTrackingSignature(newLastCompletedSearchTrackingSignature: string) {
    lastCompletedSearchTrackingSignature.value = newLastCompletedSearchTrackingSignature
  }
  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }
  function setKeywordSuggestions(newKeywordSuggestions: any[]) {
    keywordSuggestions.value = newKeywordSuggestions
  }
  function setTopEvents(newTopEvents: any[]) {
    topEvents.value = newTopEvents
  }
  function setTopHits(newTopHits: string[]) {
    topHits.value = newTopHits
  }
  function setTotalHits(newTotalHits: number) {
    totalHits.value = newTotalHits
  }
  function setPendingSearchPerformed(pending: boolean) {
    pendingSearchPerformed.value = pending
  }

  async function fetchFallbackBanners() {
    const key = `calendar-fallback-banners;${locale.value}`
    const ttl = 3600

    const fetcher = async () => {
      return (await cmsFetch(`/api/${locale.value}/concertagenda/fallback-banners.json`)).data
    }

    const fetchedFallbackBanners = await cacheStore.fetch({ key, ttl, fetcher, fallback: [] })
    setFallbackBanners(fetchedFallbackBanners)
    return fetchedFallbackBanners
  }

  async function fetchBanners() {
    const key = `calendar-banners;${locale.value};composers:${composers.value.join(',')};genres:${genres.value.join(',')};specials:${specials.value.join(',')}`
    const ttl = 3600

    // We only support showing special banners when only one of the facets is used
    let fetchedBanners: any[] = []
    const sections = [composers.value.length > 0 ? 'composer' : undefined, genres.value.length > 0 ? 'genre' : undefined, specials.value.length > 0 ? 'special' : undefined].filter(
      (section) => section,
    )
    if (sections.length === 1) {
      const fetcher = async () => {
        return (
          await cmsFetch(
            `/api/${locale.value}/banners/concertagenda/${sections[0]}/${[...composers.value, ...genres.value, ...specials.value]
              .map((value) => value.replace('/', '-SLASH-')) // There can be no slashes in the uri
              .join(',')}.json`,
          )
        ).data
      }
      fetchedBanners = await cacheStore.fetch({ key, ttl, fetcher, fallback: [] })
    }

    setBanners(fetchedBanners)
    return fetchedBanners
  }

  async function fetchMonth({ year, month }: { year: number; month: number }) {
    const key = `calendar-month;${locale.value};${year}-${month}`
    const ttl = 900

    const fetcher = async () => {
      // Calculate UTC midnight for the first of the month
      const yearStr = String(year)
      const monthStr = String(month).padStart(2, '0')
      const date = `${yearStr}-${monthStr}-01T00:00:00.000Z`
      // Calculate UTC midnight for the first of the next month
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      const nextMonthStr = String(nextMonth).padStart(2, '0')
      const endDate = `${nextYear}-${nextMonthStr}-01T00:00:00.000Z`
      const eventDateRange = `from ${date} to ${endDate}`
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query eventsInMonth($site:[String], $eventEndDateRange:[String]) {
          elasticSearch(site:$site, section:"event", eventEndDateRange:$eventEndDateRange) {
            facets(limit:1024) {
              eventDate(interval:"day") {
                value
              }
            }
          }
        }`,
        variables: { site: `${locale.value}Default`, eventEndDateRange: eventDateRange },
      })
      return elasticSearch.facets.eventDate.map(({ value }: { value: string }) => new Date(parseInt(value, 10)).toISOString())
    }

    // Fetch the aggregated days
    return await cacheStore.fetch({ key, ttl, fetcher, fallback: [] })
  }

  async function fetchAllFacets() {
    // We actually want to search for events after 'now' and also rounded to the last hour to allow for caching
    const actualDate = toHour('now').toISOString()
    const key = `calendar;${locale.value};all-facets;date:${actualDate}`
    const ttl = 900

    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query allEventFacets($site:[String], $eventEndDate:[String]) {
            elasticSearch(site:$site, section:"event", eventEndDateRange:$eventEndDate) {
              facets(limit:1024) {
                composers { label: value }
                discounts: importDiscountGroups { value }
                genres { label: value }
                instruments { label: value }
                rooms: room { label: value }
                specials { label: value }
              }
            }
          }`,
        variables: { site: `${locale.value}Default`, eventEndDate: `gte ${actualDate}` },
      })
      return elasticSearch.facets
    }

    // Fetch the facets
    const fetchedAllFacets = await cacheStore.fetch({ key, ttl, fetcher, fallback: {} })
    setAllFacets(fetchedAllFacets)
    return fetchedAllFacets
  }

  async function fetchEvents() {
    const { composers, concertTitles, date, discounts, genres, instruments, keyword, musicians, organizers, rooms, specials, tags, works } = stateSnapshot()
    const pageValue = page.value
    const hitsPerPageValue = hitsPerPage.value
    const requestId = ++latestFetchRequestId.value
    const trackingSignature = buildSearchTrackingSignature(stateSnapshot(), {
      composers,
      concertTitles,
      date,
      discounts,
      genres,
      instruments,
      keyword,
      musicians,
      organizers,
      rooms,
      specials,
      tags,
      works,
    })

    setLoading(true)

    // Hidden suggestion filters are applied through search text while still being tracked as removable pills.
    const hiddenSearchTerms = [...organizers, ...concertTitles, ...musicians, ...works].join(' ').trim()
    const effectiveKeyword = [keyword, hiddenSearchTerms]
      .filter((term) => term)
      .join(' ')
      .trim()

    fetchKeywordSuggestions()

    // We actually want to search for events after 'now' and also rounded to the last hour to allow for caching
    const actualDate = toHour(date).toISOString()
    const key = [
      'calendar',
      locale.value,
      `keyword:${keyword}`,
      `organizers:${organizers.join(',')}`,
      `concertTitles:${concertTitles.join(',')}`,
      `musicians:${musicians.join(',')}`,
      `works:${works.join(',')}`,
      `date:${actualDate}`,
      `composers:${composers.join(',')}`,
      `discounts:${discounts.join(',')}`,
      `genres:${genres.join(',')}`,
      `instruments:${instruments.join(',')}`,
      `rooms:${rooms.join(',')}`,
      `specials:${specials.join(',')}`,
      `tags:${tags.join(',')}`,
    ].join(';')
    const ttl = 300

    /*
     * Which facet categories Elasticsearch returns is data-driven, so both facet maps are typed as keyed
     * records rather than a fixed set of categories. Consumers already treat a missing category as empty.
     */
    const fetcher = async (): Promise<{
      facets: Record<string, any>
      separateFacets: Record<string, any>
      hits: string[]
      topHits: string[]
      totalHits: number
    }> => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      // For the topEvents we just retrieve the first few
      const fuzzyness = effectiveKeyword.length <= 1 ? '0' : 'AUTO'
      const esBase = `site:$site, section:"event", fuzzyness:"${fuzzyness}", eventEndDateRange:$eventEndDate, search:$keyword, importIconcertStatusFacet: $importIconcertStatus`
      const { events, topEvents, genreFacets, composerFacets, instrumentFacets, roomFacets, specialFacets, discountFacets } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query searchEvents($site:[String], $eventEndDate:[String], $keyword:String, $composers:[String], $discounts:[String], $genres:[String], $importIconcertStatus: [String], $instruments:[String], $organizers:[String], $rooms:[String], $specials:[String], $tags:[String]) {
          events: elasticSearch(${esBase}, composersFacet:$composers, importDiscountGroupsFacet:$discounts, genresFacet:$genres, instrumentsFacet:$instruments, organizerFacet:$organizers, roomFacet:$rooms, specialsFacet:$specials, tagsFacet:$tags, orderBy:"eventDate ASC") {
            facets(limit:1024) {
              composers { label: value, count }
              discounts: importDiscountGroups { value, count }
              genres { label: value, count }
              instruments { label: value, count }
              rooms: room { label: value, count }
              specials { label: value, count }
            }
            count
            hits(limit:1024) {
             uri
            }
          }
          topEvents: elasticSearch(${esBase}, composersFacet:$composers, importDiscountGroupsFacet:$discounts, genresFacet:$genres, instrumentsFacet:$instruments, organizerFacet:$organizers, roomFacet:$rooms, specialsFacet:$specials, tagsFacet:$tags) {
            hits(limit:5) {
             uri
            }
          }
          genreFacets: elasticSearch(${esBase}, composersFacet:$composers, importDiscountGroupsFacet:$discounts, instrumentsFacet:$instruments, organizerFacet:$organizers, roomFacet:$rooms, specialsFacet:$specials, tagsFacet:$tags) {
            facets(limit:1024) { genres { label: value, count } }
          }
          composerFacets: elasticSearch(${esBase}, importDiscountGroupsFacet:$discounts, genresFacet:$genres, instrumentsFacet:$instruments, organizerFacet:$organizers, roomFacet:$rooms, specialsFacet:$specials, tagsFacet:$tags) {
            facets(limit:1024) { composers { label: value, count } }
          }
          instrumentFacets: elasticSearch(${esBase}, composersFacet:$composers, importDiscountGroupsFacet:$discounts, genresFacet:$genres, organizerFacet:$organizers, roomFacet:$rooms, specialsFacet:$specials, tagsFacet:$tags) {
            facets(limit:1024) { instruments { label: value, count } }
          }
          roomFacets: elasticSearch(${esBase}, composersFacet:$composers, importDiscountGroupsFacet:$discounts, genresFacet:$genres, instrumentsFacet:$instruments, organizerFacet:$organizers, specialsFacet:$specials, tagsFacet:$tags) {
            facets(limit:1024) { rooms: room { label: value, count } }
          }
          specialFacets: elasticSearch(${esBase}, composersFacet:$composers, importDiscountGroupsFacet:$discounts, genresFacet:$genres, instrumentsFacet:$instruments, organizerFacet:$organizers, roomFacet:$rooms, tagsFacet:$tags) {
            facets(limit:1024) { specials { label: value, count } }
          }
          discountFacets: elasticSearch(${esBase}, composersFacet:$composers, genresFacet:$genres, instrumentsFacet:$instruments, organizerFacet:$organizers, roomFacet:$rooms, specialsFacet:$specials, tagsFacet:$tags) {
            facets(limit:1024) { discounts: importDiscountGroups { value, count } }
          }
        }`,
        variables: {
          site: `${locale.value}Default`,
          eventEndDate: `gte ${actualDate}`,
          keyword: effectiveKeyword,
          composers,
          discounts,
          genres,
          importIconcertStatus: ['1e Optie', 'Bevestigd', 'Contract uit', 'Corona', 'Voor brochure', 'Verplaatst', 'Geannuleerd'],
          instruments,
          organizers,
          rooms,
          specials,
          tags,
        },
      })

      return {
        facets: events.facets,
        separateFacets: {
          genres: genreFacets.facets.genres,
          composers: composerFacets.facets.composers,
          instruments: instrumentFacets.facets.instruments,
          rooms: roomFacets.facets.rooms,
          specials: specialFacets.facets.specials,
          discounts: discountFacets.facets.discounts,
        },
        hits: events.hits.map(({ uri }: { uri: string }) => uri),
        topHits: topEvents.hits.map(({ uri }: { uri: string }) => uri),
        totalHits: events.count,
      }
    }

    // Fetch the hits
    const {
      facets: fetchedFacets,
      separateFacets: fetchedSeparateFacets,
      hits: fetchedHits,
      topHits: fetchedTopHits,
      totalHits: fetchedTotalHits,
    } = await cacheStore.fetch({
      key,
      ttl,
      fetcher,
      fallback: { facets: {}, separateFacets: {}, hits: [], topHits: [], totalHits: 0 },
    })
    if (requestId !== latestFetchRequestId.value) {
      return []
    }
    setFacets(fetchedFacets)
    setSeparateFacets(fetchedSeparateFacets)
    setHits(fetchedHits)
    setTopHits(fetchedTopHits)
    setTotalHits(fetchedTotalHits)

    // todo: add loaders
    // Initialize the series list with N empty elements, used to trigger loaders
    const slice = fetchedHits.slice((pageValue - 1) * hitsPerPageValue, pageValue * hitsPerPageValue)
    // commit(
    //   'setEvents',
    //   slice.map(() => undefined)
    // )

    // Fetch all associated entries within the current page
    const fetchedEvents = await Promise.all(slice.map((uri: string) => pageStore.fetchUri({ uri })))
    if (requestId !== latestFetchRequestId.value) {
      return []
    }
    setEvents(fetchedEvents.filter((event) => event))
    setLastCompletedSearchTrackingSignature(trackingSignature)

    // Fetch all associated top hits when a keyword search was done
    // todo: temporarily disable the topEvents, this should be re-enabled once Stijn accepts the change
    // if (keyword) {
    //   const topEvents = await Promise.all(topHits.map((uri) => dispatch('page/fetchUri', { uri }, { root: true })))
    //   commit('setTopEvents', topEvents)
    // } else {
    //   commit('setTopEvents', [])
    // }

    setLoading(false)
    return fetchedEvents
  }

  // overrides.q: draft keyword from the component (before the user presses Enter/submit)
  async function fetchKeywordSuggestions(overrides: any = {}) {
    const requestSignature = buildSuggestionSignature(stateSnapshot(), overrides)
    const params = buildSuggestionParams(stateSnapshot(), overrides)

    if (params.q === '') {
      setKeywordSuggestions([])
      return []
    }

    try {
      const response = await cmsFetch(`/api/${locale.value}Default/search/concertsuggest.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      })

      // Discard stale response if state has changed since this request was fired
      if (requestSignature !== buildSuggestionSignature(stateSnapshot(), overrides)) {
        return keywordSuggestions.value
      }

      const suggestions = response?.data?.suggestions || []
      setKeywordSuggestions(suggestions)
      return suggestions
    } catch {
      if (requestSignature === buildSuggestionSignature(stateSnapshot(), overrides)) {
        setKeywordSuggestions([])
      }

      return []
    }
  }

  return {
    allFacets,
    composers,
    date,
    discounts,
    genres,
    instruments,
    keyword,
    organizers,
    concertTitles,
    musicians,
    works,
    page,
    rooms,
    specials,
    tags,
    fallbackBanners,
    banners,
    events,
    rawFacets,
    separateFacets,
    hits,
    loading,
    lastCompletedSearchTrackingSignature,
    keywordSuggestions,
    topEvents,
    topHits,
    totalHits,
    pendingSearchPerformed,
    hitsPerPage,
    dateIsSpecified,
    searchTrackingSignature,
    urlParameters,
    bannersAtPosition,
    facets,
    activeFacets,
    eventsByDay,
    setAllFacets,
    initialize,
    setUrlParameters,
    reset,
    setDate,
    setKeyword,
    toggleFacet,
    setPage,
    setFallbackBanners,
    setBanners,
    setEvents,
    setFacets,
    setSeparateFacets,
    setHits,
    setLastCompletedSearchTrackingSignature,
    setLoading,
    setKeywordSuggestions,
    setTopEvents,
    setTopHits,
    setTotalHits,
    setPendingSearchPerformed,
    fetchFallbackBanners,
    fetchBanners,
    fetchMonth,
    fetchAllFacets,
    fetchKeywordSuggestions,
    fetch: fetchEvents,
  }
})
